import { Box, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { useId } from 'react'
import { YALO_RADIUS, YALO_SEMANTIC } from '@commerce/tokens'

export interface PaymentOption {
  /** The API's own token — `CASH`, `CREDIT` — echoed back on selection. */
  id: string
  label: string
  detail: string
  /** The design's "RECOMENDADO" flag. */
  tag?: string
  /**
   * Set when the option cannot be chosen, and shown in place of `detail`.
   *
   * It is the reason, not a boolean, because a payment method that greys out
   * with no explanation is the thing that generates support tickets: the
   * shopper cannot tell a broken screen from a rule they are hitting. Making
   * the reason the only way to disable an option means the caller cannot
   * forget it.
   */
  disabledReason?: string
}

export interface PaymentMethodListProps {
  options: readonly PaymentOption[]
  value: string | null
  onChange: (id: string) => void
  /** The group's accessible name. The design draws it as an eyebrow. */
  legend: string
}

/**
 * The payment methods, as a real radio group.
 *
 * The design draws four clickable cards; this renders four radios styled as
 * those cards. That is not pedantry — a radio group is arrow-key navigable and
 * announces "2 of 4" for free, and `<div onClick>` gives up both. The card is
 * the `<label>`, so the whole surface stays clickable either way.
 *
 * A disabled option is a real `disabled` radio: arrow-key navigation skips it,
 * which is the behaviour someone tabbing through expects from an option they
 * cannot pick. Its reason is rendered in place of the detail line and wired to
 * the input through `aria-describedby`, so the reason travels with the control
 * rather than sitting next to it visually and nowhere semantically.
 */
export const PaymentMethodList = ({ options, value, onChange, legend }: PaymentMethodListProps) => {
  const groupId = useId()
  const legendId = `${groupId}-legend`

  return (
    <FormControl component="fieldset" fullWidth>
      <FormLabel
        component="legend"
        id={legendId}
        sx={{ typography: 'overline', color: YALO_SEMANTIC.fg3, mb: 1.5 }}
      >
        {legend}
      </FormLabel>
      {/* A `<legend>` inside a `<fieldset>` names the fieldset, not the
          radiogroup MUI renders inside it, so the group needs to be pointed at
          it explicitly. */}
      <RadioGroup
        aria-labelledby={legendId}
        name={`${groupId}-payment-method`}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
      >
        <Stack spacing={1.5}>
          {options.map((option) => {
            const labelId = `${groupId}-${option.id}-label`
            const detailId = `${groupId}-${option.id}-detail`
            const isSelected = option.id === value
            const isDisabled = option.disabledReason !== undefined

            return (
              <FormControlLabel
                key={option.id}
                value={option.id}
                disabled={isDisabled}
                control={
                  <Radio
                    inputProps={{ 'aria-labelledby': labelId, 'aria-describedby': detailId }}
                  />
                }
                sx={{
                  m: 0,
                  p: '13px 14px',
                  gap: 1.5,
                  alignItems: 'center',
                  bgcolor: YALO_SEMANTIC.bgSurface,
                  borderRadius: `${YALO_RADIUS.lg}px`,
                  // 1.5px in the design, and it is on every card rather than
                  // only the selected one: a border that appears on selection
                  // shifts the whole row by 1.5px.
                  border: '1.5px solid',
                  borderColor: isSelected ? 'primary.main' : YALO_SEMANTIC.borderSubtle,
                  opacity: isDisabled ? 0.45 : 1,
                }}
                label={
                  <Stack
                    component="span"
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{ width: '100%' }}
                  >
                    <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                      <Typography component="span" id={labelId} display="block" sx={{ fontWeight: 600 }}>
                        {option.label}
                      </Typography>
                      <Typography
                        component="span"
                        id={detailId}
                        display="block"
                        variant="body2"
                        sx={{ color: isDisabled ? YALO_SEMANTIC.statusDanger : YALO_SEMANTIC.fg3 }}
                      >
                        {option.disabledReason ?? option.detail}
                      </Typography>
                    </Box>
                    {option.tag && (
                      <Typography
                        component="span"
                        variant="overline"
                        sx={{ color: 'primary.dark', fontWeight: 700, whiteSpace: 'nowrap' }}
                      >
                        {option.tag}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            )
          })}
        </Stack>
      </RadioGroup>
    </FormControl>
  )
}
